<template>
	<div class="color-picker" v-click-outside="colorPickerCancel">
		<div
			ref="colorPickerRef"
			@click="colorPickerOpen"
			class="color-picker__result"
		>
			<div
				:style="{background: color}"
				class="color-picker__result-bg"
			></div>
		</div>
		<div
			class="color-picker__box"
			:class="colorPickerOpenedClassname"
			@mouseup="gradientPointerDragEnd"
		>
			<div
				class="color-picker__types"
				v-if="isMultipleMode"
			>
				<span
					v-for="mode in modes"
					:key="mode"
					class="color-picker__button"
					@click="modeChange(mode)"
					:class="{active: modeActive === mode}"
				>
					{{ COLOR_PICKER_MODES[mode.toUpperCase()].NAME }}
				</span>
			</div>
			<div
				v-if="isLinearActive || isRadialActive"
				class="color-picker__settings"
			>
				<div class="color-gradient">
					<div class="color-gradient__view">
						<div
							class="color-gradient__view-bg"
							:style="{background: gradientColor}"
						></div>
					</div>
					<div
						ref="pointersContainerRef"
						class="color-gradient__points"
						@touchmove="gradientPointerDrag"
						@mousemove="gradientPointerDrag"
					>
						<div
							ref="pointerLeftRef"
							class="color-gradient__point"
							:style="{left: `${gradientData.steps[0].position}%`, background: `${gradientData.steps[0].color}`}"
							:class="{active: gradientPointSelected === 0}"
							@click="gradientPointSetActive(0)"
							@touchstart="gradientPointerDragStart(0)"
							@mousedown="gradientPointerDragStart(0)"
							@touchend="gradientPointerDragEnd"
							@mouseup="gradientPointerDragEnd"
						>
							<span class="color-gradient__point-remove"></span>
						</div>
						<div
							ref="pointerRightRef"
							class="color-gradient__point"
							:style="{left: `${gradientData.steps[1].position}%`, background: `${gradientData.steps[1].color}`}"
							:class="{active: gradientPointSelected === 1}"
							@click="gradientPointSetActive(1)"
							@touchstart="gradientPointerDragStart(1)"
							@mousedown="gradientPointerDragStart(1)"
							@touchend="gradientPointerDragEnd"
							@mouseup="gradientPointerDragEnd"
						>
							<span class="color-gradient__point-remove"></span>
						</div>
					</div>
				</div>
				
				<div
					v-if="isLinearActive"
					class="color-picker__linear"
				>
					<div class="color-range">
						<div class="color-range__icon"></div>
						<input
							type="range" min="0" max="360" step="1"
							:value="gradientData.deg"
							@input="linearAngleSet"
							class="color-range__value">
						<input
							type="number" min="0" max="360" step="1"
							:value="gradientData.deg"
							@input="linearAngleSet"
							class="color-range__input">
					</div>
				</div>
				
				<div
					v-if="isRadialActive"
					class="color-picker__radial"
				>
					<div class="color-picker__icon color-picker__icon--radial"></div>
					<span
						:class="{active: !isRadialCircle}"
						@click="radialCircleSet(false)"
						class="color-picker__button">Ellipse</span>
					<span
						:class="{active: isRadialCircle}"
						@click="radialCircleSet(true)"
						class="color-picker__button">Circle</span>
				</div>
			
			</div>
			<div
				:class="solidModeSeparatorClassname"
				class="color-picker__main"
			>
				<div class="color-picker__icon"></div>
				<div class="color-picker__color">
					<input
						type="color"
						name="color"
						:value="solidColor"
						@input="solidColorChange"
						:style="{opacity: opacityValue}"
					>
				</div>
				<input
					:value="solidHex"
					@input="solidHexChange"
					type="text"
					autocomplete="off"
					name="hex"
					class="color-picker__value"
				>
			</div>
			<div
				v-if="isTransparencyOn"
				class="color-picker__transparency">
				<div class="color-range">
					<div class="color-range__icon color-range__icon--transparency"></div>
					<input
						type="range"
						min="0"
						max="1"
						step="0.05"
						:value="opacityValue"
						@input="transparencyChangeRange"
						class="color-range__value"
					>
					<input
						type="number"
						min="0"
						max="1"
						step="0.05"
						:value="opacityValue"
						@input="transparencyChangeField"
						class="color-range__input"
					>
				</div>
			</div>
			<div class="color-picker__control">
				<span
					@click="colorPickerCancel"
					class="color-picker__button bg--black">Cancel</span>
				<span
					@click="colorPickerSave"
					class="color-picker__button bg--dusky-rose">OK</span>
			</div>
		</div>
	
	</div>
	
</template>

<script>
import {computed, inject, onMounted, onUpdated, ref, watch} from 'vue';
import { clickOutside} from '@/directives/click-outside';
import {COLOR_PICKER_MODES} from '@/models/COLOR_PICKER_MODES';

export default {
	directives: {
		clickOutside,
	},
	props: {
		colorValue: {
			type: String,
			default: ''
		},
		modes: {
			type: Array,
			default: () => ['solid'] //['linear-gradient', 'radial-gradient']
		},
		activeMode: {
			type: String,
			default: COLOR_PICKER_MODES.SOLID.VALUE
		},
		transparency: {
			type: Boolean,
			default: false
		}
	},
	emits: ["update:colorValue"],
  setup(props, {emit}) {
  	const color = computed({
				get() {return props.colorValue},
				set(value) {emit("update:colorValue", value)}
			}
		)
		watch(
			() => props.colorValue,
			(value) => {
				if(props.transparency && value.indexOf('rgba') !== -1){
					const data = RGBA_to_hexA(value);
					opacityValue.value = data[1];
				} else {
					opacityValue.value = 1;
				}
		})
  	const colorPickerRef = ref(null);
  	// background: linear-gradient(rgba(181, 234, 234, 0.55) 0%, rgb(131, 14, 163) 100%);
  	
  	const settingsDefaults = {
  		solidColor: '#390283',
			gradientColor: 'linear-gradient(180deg, #b5eaea 0%, #830EA3 100%)',
			linearColor: 'linear-gradient(180deg, #b5eaea 0%, #830EA3 100%)',
			radialColor: 'radial-gradient(circle, #B5EAEA 1%, #830EA3 99%)',
			linearLeftPoint: '#224474',
			linearRightPoint: '#871132',
			gradientData: {
				deg: 0,
				radialCircle: false,
				steps: []
			}
		}
  	
  	// transparency
  
		const opacityValue = ref(1);
  	function opacityChange(opacity){
  		opacityValue.value = opacity;
		}
  	function transparencyChangeRange(event){
  		const currentValue = event.target.value;
  		opacityValue.value = currentValue;
		
			if(isTransparencyOn.value && (isLinearActive.value || isRadialActive.value)){
				gradientPointSetOpacity(currentValue);
				gradientBuild();
			}
		}
		function transparencyChangeField(event){
			const currentValue = event.target.value;
			opacityValue.value = currentValue;
			
			if(isTransparencyOn.value && (isLinearActive.value || isRadialActive.value)){
				gradientPointSetOpacity(currentValue);
				gradientBuild();
			}
		}
  	
  	// modes control
  	const modeActive = ref(null);
  	const isMultipleMode = computed(() => props.modes.length > 1);
  	const isLinearAvailable = computed(()=> props.modes.includes(COLOR_PICKER_MODES.LINEAR.VALUE));
  	const isLinearActive = computed(()=> modeActive.value === COLOR_PICKER_MODES.LINEAR.VALUE);
  	const isRadialAvailable = computed(() => props.modes.includes(COLOR_PICKER_MODES.RADIAL.VALUE));
  	const isRadialActive = computed(() => modeActive.value === COLOR_PICKER_MODES.RADIAL.VALUE);
  	const solidModeSeparatorClassname = computed(() => isLinearActive.value || isRadialActive.value ? 'separator' : '');
  	
  	function modeChange(mode){
  		modeActive.value = mode;
  		
  		switch (mode){
				case COLOR_PICKER_MODES.SOLID.VALUE: {
					const colorSolid = !color.value?.includes('linear-gradient') && !color.value?.includes('radial-gradient') ? color.value : settingsDefaults.solidColor;
					solidInit(colorSolid);
					break;
				}
  			case COLOR_PICKER_MODES.LINEAR.VALUE: {
					const colorLinear = color.value?.includes('linear-gradient') ? color.value : settingsDefaults.linearColor;
					gradientInit(colorLinear); // updates gradientData.value
					linearAngleInit(gradientData.value.deg);
					gradientPointSetActive(0);
					gradientBuild(false); // applies gradient styles
					break;
				}
				case COLOR_PICKER_MODES.RADIAL.VALUE: {
					const colorRadial = color.value?.includes('radial-gradient') ? color.value : settingsDefaults.linearColor;
					gradientInit(colorRadial); // updates gradientData.value
					gradientPointSetActive(0);
					gradientBuild(false); // applies gradient styles
					break;
				}
			}
			
			
			
  		// to set active left one gradient range point
  		// to change related solid color
		}
		function modeInit(){
			if(color.value?.includes('linear-gradient')) {
				modeChange(COLOR_PICKER_MODES.LINEAR.VALUE);
				// solidInit(settingsDefaults.linearLeftPoint);
				const colorToInit = color.value.includes('linear-gradient') ? color.value : settingsDefaults.linearColor;
				gradientInit(colorToInit);
				gradientBuild(false);
			}
			else if(color.value?.includes('radial-gradient')) {
				modeChange(COLOR_PICKER_MODES.RADIAL.VALUE);
				const colorToInit = color.value.includes('radial-gradient') ? color.value : settingsDefaults.radialColor;
				gradientInit(colorToInit);
				gradientBuild(false);
			}
			else {
				modeChange(COLOR_PICKER_MODES.SOLID.VALUE);
				solidInit(solidHex.value || color.value || settingsDefaults.solidColor);
			}
		}
		
  	const isTransparencyOn = computed(()=> props.transparency);
		// color picker box control
  	const isColorPickerOpened = ref(false);
  	const colorPickerOpenedClassname = computed(() => isColorPickerOpened.value ? "active" : "");
  	function colorPickerOpen(){
			isColorPickerOpened.value = true;
			modeInit();
		}
  	function colorPickerClose(){
			isColorPickerOpened.value = false;
			pointerIsDragging.value = false; // disable pointer dragging anchor in case of accidental colorpicker closing
		}
		function colorPickerCancel(){
  		// set init colors values
			colorPickerClose();
		}
		
		function colorPickerSave(){
  		// apply color ...
			if(modeActive.value === COLOR_PICKER_MODES.SOLID.VALUE){
				props.transparency ? color.value = hex_to_RGBA(solidHex.value, opacityValue.value) : color.value = solidColor.value;
			}
			if(isLinearActive.value){
				color.value = gradientColor.value;
			} else if(isRadialActive.value){
				color.value = gradientColor.value;
			} /*else {
				console.log('Another FROM HERE');
				isTransparencyOn.value ? color.value = hex_to_RGBA(solidHex.value, opacityValue.value) : color.value = solidColor.value;
			}*/
  		colorPickerClose();
		}
	
		/* elaborate solid color data (color and alpha) loading into solidColor and opacityValue */
		const solidColor = ref(null);
		const solidHex = ref(null);
		
		function solidInit(rawData) {
			if(!rawData) return;
		
			// rgba
			if(rawData.indexOf('rgba') !== -1) {
				const data = RGBA_to_hexA(rawData);
				solidColor.value = data[0];
				solidHex.value = data[0];
				opacityValue.value = data[1];
			}
			// rgb
			else if(rawData.indexOf('rgb') !== -1) {
				solidColor.value = solidHex.value = RGB_to_hex(rawData);
			}
			// hex
			else {
				solidColor.value = solidHex.value = short_hex_fix(rawData);
			}
		}
		function solidColorChange(event){
			const updatedColor = event.target.value;
			solidColor.value = solidHex.value = updatedColor.toUpperCase();
			
			if(isLinearActive.value || isRadialActive.value){
				gradientPointSetColor(updatedColor);
				gradientBuild();
			}
		}
		const hexTimeout = ref(null);
		function solidHexChange(event){
			if(hexTimeout.value) clearTimeout(hexTimeout.value);
			hexTimeout.value = setTimeout(() => {
				let val = short_hex_fix(event.target.value);
				val.match(/^#[a-f0-9]{6}$/i) === null ?
					event.target.value = solidHex.value = solidColor.value.toUpperCase() :
					solidHex.value = solidColor.value = val;
				// apply input value to gradient if active
				if(isLinearActive.value || isRadialActive.value){
					gradientPointSetColor(solidHex.value);
					gradientBuild();
				}
			}, 700);
		}
		
		/* gradient */
		const gradientColor = ref(null);
		const gradientPointSelected = ref(0);
		
		const gradientData = ref(settingsDefaults.gradientData);
	
		/* elaborate gradient data loading into gradientData */
		function gradientInit(rawData) {
			if(!rawData) return;
			const isRadial = (rawData.includes('radial-gradient'));
		
			// solve issues with inner RGB|RGBA and turn everything into RGBA
			rawData = rawData
				.replace(/,\./g, ',0.').replace(/ \./g, ' 0.')
				.replace(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)/g, 'rgbaZ($1|$2|$3|$4)')
				.replace(/\|\)/g, '|1)');
		
			// names to deg
			rawData = rawData
				.replace('top right', '45deg').replace('right top', '45deg')
				.replace('bottom right', '135deg').replace('bottom right', '135deg')
				.replace('top left', '315deg').replace('left top', '315deg')
				.replace('bottom left', '225deg').replace('bottom left', '225deg')
				.replace('right', '90deg').replace('left', '270deg').replace('top', '0deg').replace('bottom', '180deg');
		
			// be sure deg or shape is defined
			if(isRadial && rawData.indexOf('ellipse') === -1 && rawData.indexOf('circle') === -1) {
				rawData.replace('\\(', '(ellipse ');
			}
			if(!isRadial && rawData.indexOf('deg') === -1) {
				rawData.replace('\\(', '(180deg');
			}
		
			// process
			rawData = replaceColorsInArray(
				rawData,
				['linear-gradient', 'radial-gradient', '', '\\(', 'to', '\\)'],
				''
			);
		
			// split steps
			const rawSteps = rawData.split(',');
			const fallbackMultiplier = 100 / rawSteps.length;
			
			gradientData.value.steps = [];
			rawSteps.some(function(rawStep, index) {
			
				// direction on first index
				if(!index) {
					isRadial ?
						gradientData.value.radialCircle = (rawStep.indexOf('circle') !== -1) :
						gradientData.value.deg = parseInt(rawStep.replace('deg', ''), 10);
				}
			
				// {color : null, opacity: null, position : null}
				else {
					rawStep = rawStep.trim().split(' ');
					let position;
				
					// position
					if(rawStep.length < 2) {
						if(index === 1) {
							position = '0%';
						}
						else if(index === (rawSteps.length - 1)) {
							position = '100%';
						}
						else {
							position = (fallbackMultiplier * index) +'%';
						}
					}
					else {
						position = rawStep[1];
					}
				
					// color
					let rawColor = rawStep[0],
						opacity = 1;
				
					// normalize to hex
					if(rawColor.indexOf('rgbaZ') !== -1) {
						const colorArray = RGBA_to_hexA(
							rawColor.replace('rgbaZ', 'rgba').replace(/\|/g, ',')
						);
					
						rawColor = colorArray[0];
						opacity = colorArray[1];
					}
				
					gradientData.value.steps.push({
						color : short_hex_fix(rawColor),
						opacity: opacity,
						position : parseInt(position, 10)
					});
				}
			});
		}
		
		// gradient points
		const pointersContainerRef = ref(null);
		const pointerIsDragging = ref(false);
		const pointerLeftRef = ref(null);
		const pointerRightRef = ref(null);
		function gradientPointSetActive(index){
			gradientPointSelected.value = index;
			
			solidInit(gradientData.value.steps[index].color);
			opacityChange(gradientData.value.steps[index].opacity);
		}
		function gradientPointSetColor(color){
			gradientData.value.steps[gradientPointSelected.value].color = color;
		}
		function gradientPointSetOpacity(opacity){
			gradientData.value.steps[gradientPointSelected.value].opacity = opacity;
		}
		
		function gradientPointerDragStart(pointerIndex){
			gradientPointSelected.value = pointerIndex;
			pointerIsDragging.value = true;
			
		}
		function gradientPointerDrag(event){
			// todo handle moving pointer outside colorpicker area while dragging
			if(!pointerIsDragging.value) return;
			const rect = pointersContainerRef.value.getBoundingClientRect();
			
			let new_pos = (event.type === "touchmove") ? (event.touches[0].clientX - rect.left) : (event.clientX - rect.left);
			new_pos = Math.round((100 * new_pos) / pointersContainerRef.value.offsetWidth);
			
			if(new_pos < 0) {new_pos = 0;}
			else if(new_pos > 100) {new_pos = 100;}
			
			// limit positions basing on previous and next step
			const min_pos = (!gradientPointSelected.value) ? 0 : gradientData.value.steps[ gradientPointSelected.value - 1 ].position;
			const max_pos = (gradientPointSelected.value === (gradientData.value.steps.length - 1)) ? 100 : gradientData.value.steps[ gradientPointSelected.value + 1 ].position;
			
			if(new_pos <= min_pos) {new_pos = min_pos + 1;}
			else if(new_pos >= max_pos) {new_pos = max_pos - 1;}
			
			gradientData.value.steps[ gradientPointSelected.value ].position = new_pos;
			
			gradientBuild();
		}
		function gradientPointerDragEnd(){
			pointerIsDragging.value = false;
		}
		
		// linear angle
		function linearAngleInit(deg){
			gradientData.value.deg = deg;
		}
		function linearAngleSet(event){
			gradientData.value.deg = event.target.value;
			gradientBuild();
		}
		
		// radial
		const isRadialCircle = computed(() => gradientData.value.radialCircle);
		function radialCircleSet(value){
			gradientData.value.radialCircle = value;
			gradientBuild();
		}
		
		// /* apply changes to gradient, after a color/opacity/degree update */
		function gradientBuild() {
			
			let colors_part = []
			gradientData.value.steps.some(function(step, index) {
				
				let to_add = isTransparencyOn.value ? hex_to_RGBA(step.color, step.opacity) : step.color;
				to_add += ' ' + step.position +'%';
				
				colors_part.push( to_add );
			});
			
			gradientColor.value = `${COLOR_PICKER_MODES[modeActive.value.toUpperCase()].TYPE}(${isLinearActive.value ? gradientData.value.deg + 'deg' : gradientData.value.radialCircle ? 'circle' : 'ellipse'}, ${colors_part.join(', ')})`;
		}
		
		// helpers functions, todo -> move to import file
		function replaceColorsInArray(rawData, find, replace) {
			let replaceString = rawData;
		
			for (let i = 0; i < find.length; i++) {
				const regex = new RegExp(find[i], "g");
				replaceString = (typeof(replace) == 'object') ? replaceString.replace(regex, replace[i]) : replaceString.replace(regex, replace);
			}
			return replaceString;
		}
		/* handles RGBA string returning a two elements array: hex and alpha  */
		function RGBA_to_hexA(rawData) {
			rawData = replaceColorsInArray(rawData,['rgba', '\\(', '\\)'], '');
			const rgba_arr = rawData.split(',')
		
			let alpha = rgba_arr[3];
			if(alpha.substring(0, 1) === '.') {
				alpha = 0 + alpha;
			}
			rgba_arr.splice(3, 1);
		
			return [
				RGB_to_hex('rgb('+ rgba_arr.join(',') +')'),
				parseFloat(alpha)
			];
		}
	
		/* convert RGB to hex */
		function RGB_to_hex(rgb) {
			rgb = replaceColorsInArray(rgb,['rgb', '\\(', '\\)'], '');
			const rgb_arr = rgb.split(',');
		
			let r = parseInt(rgb_arr[0].trim(), 10).toString(16),
				g = parseInt(rgb_arr[1].trim(), 10).toString(16),
				b = parseInt(rgb_arr[2].trim(), 10).toString(16);
		
			if (r.length === 1) {r = "0" + r;}
			if (g.length === 1) {g = "0" + g;}
			if (b.length === 1) {b = "0" + b;}
		
			return "#" + r + g + b;
		}
	
		/* convert short hex to full format */
		function short_hex_fix(hex) {
			if(hex.length === 4) {
				const a = hex.split('');
				hex = a[0] + a[1] + a[1] + a[2] + a[2] + a[3] + a[3];
			}
		
			return hex.toUpperCase();
		}
		
		/* convert hex to RGB */
		function hex_to_RGB(h) {
			let r = 0, g = 0, b = 0;
		
			// 3 digits
			if (h.length === 4) {
				r = "0x" + h[1] + h[1];
				g = "0x" + h[2] + h[2];
				b = "0x" + h[3] + h[3];
			
				// 6 digits
			} else if (h.length === 7) {
				r = "0x" + h[1] + h[2];
				g = "0x" + h[3] + h[4];
				b = "0x" + h[5] + h[6];
			}
		
			return "rgb("+ +r + "," + +g + "," + +b + ")";
		}
	
		/* convert hex to RGBA */
		function hex_to_RGBA(h, opacity) {
			if(opacity === 1) {
				return h;
			}
		
			let rgb = hex_to_RGB(h);
			return rgb.replace('(', 'a(').replace(')', ',' + opacity + ')');
		}
  	
  	return {
			color,
  		colorPickerRef,
		
			opacityValue,
			transparencyChangeRange,
			transparencyChangeField,
		
			solidColor,
			solidHex,
			solidHexChange,
			solidColorChange,
		
			linearAngleSet,
			
			gradientData,
		
			pointersContainerRef,
			pointerLeftRef,
			pointerRightRef,
			gradientPointerDragStart,
			gradientPointerDrag,
			gradientPointerDragEnd,
		
			gradientPointSetActive,
			gradientPointSelected,
			gradientColor,
		
			COLOR_PICKER_MODES,
			modeActive,
			isMultipleMode,
			isLinearActive,
			isLinearAvailable,
			isRadialActive,
			isRadialAvailable,
			isRadialCircle,
			radialCircleSet,
			solidModeSeparatorClassname,
			modeChange,
		
			isTransparencyOn,
		
			isColorPickerOpened,
			colorPickerOpenedClassname,
			colorPickerOpen,
			colorPickerCancel,
			colorPickerSave,
		}
  }
}
</script>

<style lang="scss">
@use "@/scss/sections/editor/elements/custom/color-picker";
</style>
